<div *ngIf="hasAccess()"
  class="audit-log-container card-bg rounded-xl overflow-hidden flex flex-col h-full max-h-[calc(100vh-200px)] font-sans transition-all duration-300 ease-in-out">
  <!-- Header -->
  <div *ngIf="showTitle"
    class="header p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-900 z-20 cursor-pointer select-none"
    (click)="toggleExpand()">
    <div class="flex items-center gap-3">
      <div class="transition-transform duration-300" [class.rotate-[-90deg]]="!isExpanded()">
        <app-icon name="chevronDown" [size]="18" class="text-slate-400"></app-icon>
      </div>
      <div class="flex items-center gap-2">
        <app-icon name="clock" [size]="18" class="text-slate-500"></app-icon>
        <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">{{ title }}</h3>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <button
        class="filter-toggle cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 p-2 rounded-lg transition-all group active:scale-95"
        [ngClass]="isFilterVisible() ? 'bg-indigo-50 dark:bg-indigo-500/20' : ''" (click)="toggleFilter($event)" title="Toggle Filters">
        <app-icon name="filter" [size]="16"
          [ngClass]="isFilterVisible() ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 group-hover:text-indigo-600 dark:text-indigo-400'"></app-icon>
      </button>
      <button
        class="refresh-action cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 p-2 rounded-lg transition-all group active:scale-95"
        (click)="$event.stopPropagation(); fetchAuditLogs()" title="Refresh Logs">
        <app-icon name="refreshCcw" [size]="16"
          [ngClass]="isLoading ? 'animate-spin text-indigo-600 dark:text-indigo-400' : 'text-slate-500 group-hover:text-indigo-600 dark:group-hover:text-white'"></app-icon>
      </button>
    </div>
  </div>

  <!-- Filter Bar -->
  <div *ngIf="isExpanded() && isFilterVisible()"
    class="filter-bar p-3 bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-3 animate-in slide-in-from-top duration-200">

    <!-- Product Filter -->
    <div *ngIf="showProductFilter" class="min-w-[160px] flex-grow md:flex-grow-0">
      <app-dropdown [options]="productOptions()"
        [selectedValue]="filterProductPid() ? filterProductPid()!.toString() : ''" placeholder="All Products" size="md"
        variant="themeBase" [clearable]="true" triggerClass="h-[41px] !min-h-[41px]" rounded="xl" [tooltipFixedStrategy]="true" 
        (selectionChange)="onProductChange($event)">
      </app-dropdown>
    </div>

    <!-- Action Filter -->
    <div class="min-w-[140px] flex-grow md:flex-grow-0">
      <app-dropdown [options]="actionOptions" [selectedValue]="filterAction()" selectionMode="multiple"
        selectionStyle="checkbox" multiSelectDisplayMode="count" placeholder="All Actions" size="md" variant="themeBase"
        [clearable]="true" triggerClass="h-[41px] !min-h-[41px]" rounded="xl" [tooltipFixedStrategy]="true" 
        (selectionChange)="onActionChange($event)">
      </app-dropdown>
    </div>

    <!-- Date Range Filter -->
    <div class="min-w-[180px] flex-grow md:flex-grow-0">
      <app-date-picker (dateRangeChange)="onDateChange($event)" variant="outline"
        [showGroupBy]="false" [showComparison]="false" placeholder="Filter by date" [closeOnScroll]="true">
      </app-date-picker>
    </div>

    <!-- Apply & Clear Buttons -->
    <div class="flex items-end gap-3 ml-auto">
      <app-button variant="primary" (buttonClick)="applyFilters()"
        className="h-[41px] px-6 text-white bg-[#AD46FF] hover:bg-[#AD46FF]/90 transition-all duration-200 shadow-md shadow-[#AD46FF]/20 rounded-xl flex items-center justify-center">
        <span class="font-medium whitespace-nowrap">Apply Filters</span>
      </app-button>

      <app-button
        [disabled]="!isFiltersApplied()"
        variant="secondary" (buttonClick)="clearFilters()"
        className="h-[41px] px-4 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 rounded-xl transition-all duration-200 flex items-center justify-center">
        <span class="font-medium whitespace-nowrap">Clear Filters</span>
      </app-button>
    </div>
  </div>

  <!-- Content Area -->
  <ng-container *ngIf="isExpanded()">
    <div
      class="list-content flex-grow overflow-y-auto relative min-h-[300px] scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-800 animate-in slide-in-from-top-2 duration-300">
      <!-- Custom CSS Loader -->
      <div *ngIf="isLoading"
        class="absolute inset-0 z-30 bg-white/60 dark:bg-slate-900/60 backdrop-blur-[2px] flex items-center justify-center transition-opacity duration-300">
        <div class="flex gap-1.5 item-center justify-center">
          <div class="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-pulse [animation-delay:-0.3s]"></div>
          <div class="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-pulse [animation-delay:-0.15s]"></div>
          <div class="w-2.5 h-2.5 bg-indigo-600 rounded-full animate-pulse"></div>
        </div>
      </div>

      <!-- No data state -->
      <div *ngIf="!isLoading && auditLogs.length === 0"
        class="flex flex-col items-center justify-center py-16 px-6 text-center animate-in fade-in zoom-in duration-500">
        <div
          class="w-20 h-20 bg-slate-50 dark:bg-slate-800/50 rounded-3xl flex items-center justify-center mb-5 ring-1 ring-slate-200 dark:ring-slate-800">
          <app-icon name="clipboard" [size]="32" class="text-slate-300 dark:text-slate-600"></app-icon>
        </div>
        <p class="text-slate-600 dark:text-slate-300 font-semibold text-lg">No activity found</p>
        <p class="text-sm text-slate-400 dark:text-slate-500 mt-2 max-w-[200px]">Logs for this entity will appear here
          once actions are performed.</p>
      </div>

      <!-- Audit logs list -->
      <div *ngIf="auditLogs.length > 0" class="divide-y divide-slate-100 dark:divide-slate-800/60">
        <div *ngFor="let log of auditLogs; let i = index"
          class="log-item p-4 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-all duration-200 border-l-[3px] border-l-transparent hover:border-l-indigo-500 group relative">
          <div class="flex gap-4">
            <div
              [class]="'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 ' + getActionColor(log.action)">
              <app-icon [name]="getActionIcon(log.action)" [size]="14"></app-icon>
            </div>
            <div class="flex-grow min-w-0">
              <div class="flex items-center justify-between mb-1.5">
                <span
                  class="text-sm font-bold text-slate-800 dark:text-slate-100 group-hover:text-indigo-600 dark:group-hover:text-white transition-colors tracking-tight first-letter:uppercase">{{
                  log.actionTitle || log.action }}</span>
                <div class="flex flex-col items-end leading-none translate-y-[-2px]">
                  <span class="text-[11px] font-bold text-slate-700 dark:text-slate-300 tracking-tight">
                    {{ log.createdAt | date: 'EEEE, MMM d, y, h:mm a' : '-0700' }} PST
                  </span>
                  <span class="text-[10px] font-medium text-slate-400 dark:text-slate-500 mt-1">
                    ({{ log.createdAt | date: 'MMM d, h:mm a' : '+0530' }} IST)
                  </span>
                </div>
              </div>

              <p *ngIf="log.note"
                class="text-sm text-slate-600 dark:text-slate-400 mb-3 leading-relaxed break-words font-medium">
                {{ log.note }}
              </p>

              <!-- Update Changes -->
              <div *ngIf="getUpdateChanges(log).length > 0"
                class="mb-4 overflow-hidden rounded-xl border border-slate-100 dark:border-slate-800/60 bg-white dark:bg-slate-900/40 shadow-sm">
                <div *ngIf="getUpdateMainId(log) as main"
                  class="bg-slate-50/80 dark:bg-slate-800/50 px-3 py-1.5 flex items-center justify-between border-b border-slate-100 dark:border-slate-800/60">
                  <span class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{{
                    main.key }}: {{ main.value }}</span>
                  <span class="text-[9px] font-bold text-indigo-500/60 uppercase tracking-tighter">Modified</span>
                </div>
                <div class="p-3 space-y-2.5">
                  <div *ngFor="let change of getUpdateChanges(log)" class="group/change">
                    <div class="flex items-center justify-between mb-1">
                      <span class="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-tight">{{
                        change.key }}</span>
                    </div>
                    <div class="flex items-center gap-2 bg-slate-50/50 dark:bg-slate-800/30 p-2 rounded-lg border border-dashed border-slate-200 dark:border-slate-700/50">
                      <div class="flex-grow min-w-0">
                        <span class="block text-[11px] text-slate-400 dark:text-slate-500 line-through truncate">{{ change.old !== null ? change.old : 'null' }}</span>
                      </div>
                      <app-icon name="arrowRight" [size]="12" class="text-slate-300 dark:text-slate-600 flex-shrink-0 transition-transform group-hover/change:translate-x-0.5"></app-icon>
                      <div class="flex-grow min-w-0">
                        <span class="block text-[11px] text-green-600 dark:text-green-500 font-bold truncate">{{ change.new !== null ? change.new : 'null' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <div
                  class="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-800/80 px-2.5 py-1 rounded-lg border border-slate-200/50 dark:border-slate-700/50">
                  <app-icon name="user" [size]="12" class="text-slate-500"></app-icon>
                  <span class="text-[11px] text-slate-600 dark:text-slate-400 font-bold">{{ log.actorName || 'System'
                    }}</span>
                </div>

                <div *ngIf="log.requestId"
                  class="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-800/80 px-2.5 py-1 rounded-lg border border-slate-200/50 dark:border-slate-700/50">
                  <app-icon name="squareTerminal" [size]="12" class="text-slate-500"></app-icon>
                  <span class="text-[11px] text-slate-500 dark:text-slate-500 font-mono tracking-tighter">{{
                    log.requestId.substring(0, 8) }}</span>
                </div>

                <!-- Source Badge -->
                <span
                  class="text-[10px] text-slate-400 font-bold uppercase tracking-widest bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700/50">{{
                  log.source }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Pagination -->
    <div
      class="footer p-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-sm z-20"
      *ngIf="totalItems > limit">
      <app-pagination [totalItems]="totalItems" [pageSize]="limit" [currentPage]="currentPage"
        (pageChange)="onPageChange($event)"></app-pagination>
    </div>
  </ng-container>
</div>